import { api } from '../../../../lib/api';
import type {
  PortalComparisonMode,
  PortalGoogleReport,
  PortalGrouping,
  MetaGoalBucket,
  PortalMetaReport,
  PortalOverviewReport,
} from '../../../../types/portalReport';
import type { PortalWebsiteReport } from '../../../../types/portalWebsite';

// CLIENT PORTAL Phase 1 — the report's fetches. All under /api/portal, behind
// requirePortalAuth AND the `reporting` permission gate.
//
// ⚠️ Nothing here may reach a staff endpoint. The two audiences are kept in
// separate files across this module precisely so importing the wrong helper
// looks wrong at the import line.

export interface ReportParams {
  from: string;
  to: string;
  grouping: PortalGrouping;
  /**
   * The client's today. ⚠️ Sent so "the current month" is theirs — a report
   * opened in Auckland on the 1st must not mark the previous month partial. It
   * is display-only and cannot widen anything the server returns.
   */
  today: string;
  /** Undefined = no filter. An EMPTY array means "filtered to nothing". */
  campaignIds?: string[];
  /** A single location to drill into. Narrows server-side; never widens. */
  accountId?: string | null;
  /**
   * SEVERAL locations to include (§9). ⚠️ `null`/absent = no filter; an EMPTY
   * array = "filtered to nothing", exactly like `campaignIds`. Narrows
   * server-side, and one id outside the client's scope 404s the whole request.
   */
  accountIds?: string[] | null;
  /** Which window the headline deltas compare against. */
  comparison?: PortalComparisonMode;
  /**
   * The goal-split Meta page's campaign-goal filter (creatives + audience).
   * Undefined = no filter. The server ignores it for a scope without that page.
   */
  goalBucket?: MetaGoalBucket;
  /** Website Analytics' studio picker. ⚠️ A request only — the server decides. */
  studio?: string | null;
}

/**
 * ⚠️ EXPORTED so the `empty ≠ absent` rule is assertable at the seam it actually
 * lives on — the same reason `chartRows` is exported on the chart side. This is
 * the exact line an "omit it if it's empty" tidy-up would be written on.
 */
export function toQuery(params: ReportParams): string {
  const q = new URLSearchParams({
    from: params.from,
    to: params.to,
    grouping: params.grouping,
    today: params.today,
  });
  // ⚠️ `undefined` is omitted (no filter); an empty array is sent as an EMPTY
  // VALUE, which the server parses to `[]` — "filtered to nothing". Collapsing
  // the two would turn "I deselected everything" into "show me everything".
  if (params.campaignIds !== undefined) q.set('campaignIds', params.campaignIds.join(','));
  // ⚠️ SENT EVEN WHEN EMPTY, for the same reason `campaignIds` is: an empty
  // value is a deliberate "none of them", and omitting it would ask the server
  // for everything.
  if (params.accountIds != null) q.set('accountIds', params.accountIds.join(','));
  if (params.accountId) q.set('accountId', params.accountId);
  if (params.comparison) q.set('comparison', params.comparison);
  if (params.goalBucket) q.set('goal', params.goalBucket);
  if (params.studio) q.set('studio', params.studio);
  return q.toString();
}

export async function fetchPortalOverview(params: ReportParams): Promise<PortalOverviewReport> {
  return api<PortalOverviewReport>(`/api/portal/report/overview?${toQuery(params)}`);
}

export async function fetchPortalMetaReport(params: ReportParams): Promise<PortalMetaReport> {
  return api<PortalMetaReport>(`/api/portal/report/meta?${toQuery(params)}`);
}

export async function fetchPortalGoogleReport(params: ReportParams): Promise<PortalGoogleReport> {
  return api<PortalGoogleReport>(`/api/portal/report/google?${toQuery(params)}`);
}

export async function fetchPortalWebsiteReport(params: ReportParams): Promise<PortalWebsiteReport> {
  return api<PortalWebsiteReport>(`/api/portal/report/website?${toQuery(params)}`);
}

/** Whether this client has a report at all — drives the shell's link. */
export async function fetchPortalReportingStatus(): Promise<{
  ready: boolean;
  accountCount: number;
}> {
  return api<{ ready: boolean; accountCount: number }>('/api/portal/reporting/status');
}
